<template>
	<div>
		<div v-if="!moduleEnabled" class="mb-5">
			<vx-card>
				<vs-alert color="warning">
					<template>
						Module niet geactiveerd
					</template>
					De Voorraadpools module is nog niet geactiveerd. Activeer deze eerst in de
					<router-link to="/modules/voorraad/voorraadpools" style="color: inherit; text-decoration: underline;">module instellingen</router-link>
					.
				</vs-alert>
			</vx-card>
		</div>

		<div v-if="moduleEnabled">
			<div v-if="pool">
				<vx-card :title="`${pool.name}`" class="mb-5">
					<template slot="actions">
						<vs-button
							icon="edit"
							size="small"
							color="primary"
							type="border"
							@click="openEditPoolDialog"
						></vs-button>
					</template>

					<div class="vx-row">
						<div class="vx-col w-full mb-5">
							<p class="pool-info-text">{{ pool.description || '-' }}</p>
						</div>
					</div>
				</vx-card>

				<vx-card v-if="pool" title="Beschikbare Voorraad" class="mb-5">
					<div class="vx-row">
						<div class="vx-col w-full mb-5">
							<div class="stock-summary">
								<div class="stock-item highlight">
									<div class="stock-label">Beschikbare voor verdeling</div>
									<div class="stock-value success">{{ (pool.central_stock || 0) - (pool.safety_stock || 0) }}</div>
								</div>
								<div class="stock-item info">
									<div class="stock-label">Centrale Voorraad</div>
									<div class="stock-value primary">{{ pool.central_stock || 0 }}</div>
								</div>
								<div class="stock-item">
									<div class="stock-label">Veiligheidsvoorraad</div>
									<div class="stock-value info">{{ pool.safety_stock || 0 }}</div>
								</div>
							</div>
						</div>
					</div>
				</vx-card>

				<vx-card v-if="pool && pool.pool_eans" title="Pool EANs" class="mb-5">
					<template slot="actions">
						<vs-button color="primary" type="filled" @click="openCreateEanDialog">
							<i class="fas fa-plus mr-2"></i> Nieuwe EAN
						</vs-button>
					</template>

					<div class="vx-row">
						<div v-if="pool.pool_eans.length === 0" class="vx-col w-full text-center text-grey py-10">
							Geen EANs gekoppeld aan deze pool
						</div>

						<div v-else class="vx-col w-full">
							<table class="w-full eans-table">
								<thead>
								<tr class="border-b-2">
									<th class="text-center p-4 pl-2">Actief</th>
									<th class="text-left p-4">EAN</th>
									<th class="text-center p-4">Gewicht</th>
									<th class="text-center p-4">Bundel aantal</th>
									<th class="text-center p-4">Voorraad verdeling</th>
									<th class="text-center p-4">Ean voorraad</th>
									<th class="text-center p-4">Acties</th>
								</tr>
								</thead>
								<tbody>
								<tr v-for="ean in pool.pool_eans" :key="ean.id" class="border-b ean-row">
									<td class="text-center p-4">
										<vs-chip :color="ean.is_enabled ? 'success' : 'danger'" size="small">
											{{ ean.is_enabled ? 'Ja' : 'Nee' }}
										</vs-chip>
									</td>
									<td class="p-4">{{ ean.ean }}</td>
									<td class="text-center p-4">{{ ean.weight }}</td>
									<td class="text-center p-4">{{ ean.bundle_multiplier }}</td>
									<td class="text-center p-4 font-bold">{{ calculateEanStock(ean) }}</td>
									<td class="text-center p-4 font-bold">{{ Math.floor(calculateEanStock(ean) / ean.bundle_multiplier) }}</td>
									<td class="text-center p-4 ">
										<div class="flex justify-center gap-2">
											<vs-button
												icon="edit"
												size="small"
												color="primary"
												type="border"
												@click="openEditEanDialog(ean)"
												class="mr-2"
											></vs-button>
											<vs-button
												icon="delete"
												size="small"
												color="danger"
												type="border"
												@click="openDeleteEanDialog(ean)"
											></vs-button>
										</div>
									</td>
								</tr>
								<tr class="border-t-2 border-b-2 totals-row">
									<td colspan="4" class="text-right p-4 font-bold">Totaal Voorraad:</td>
									<td class="text-center p-4 font-bold">{{ calculateTotalEanStock() }}</td>
									<td class="text-center p-4 font-bold">{{ calculateTotalAanbodStock() }}</td>
									<td class="text-center p-4"></td>
								</tr>
								</tbody>
							</table>
						</div>
					</div>
				</vx-card>

				<vx-card v-if="pool" title="Pool wijzigingen" class="mb-5">
					<template slot="actions">
						<vs-button color="primary" type="filled" @click="openCreateMovementDialog">
							<i class="fas fa-plus mr-2"></i> Nieuwe wijziging
						</vs-button>
					</template>
					<div class="vx-row">
						<div v-if="poolMovements.length === 0" class="vx-col w-full text-center text-grey py-10">
							Geen bewegingen geregistreerd
						</div>

						<div v-else class="vx-col w-full">
							<table class="w-full movements-table">
								<thead>
								<tr class="border-b-2">
									<th class="text-left p-4">Datum</th>
									<th class="text-left p-4">Richting</th>
									<th class="text-center p-4">Hoeveelheid</th>
									<th class="text-left p-4">Reden</th>
								</tr>
								</thead>
								<tbody>
								<tr
									v-for="movement in poolMovements"
									:key="movement.id"
									:class="['border-b', 'movement-row', { 'new-movement': newMovementIds.includes(movement.id) }]"
								>
									<td class="p-4">{{ formatDate(movement.created_at) }}</td>
									<td class="p-4">
										<vs-chip :color="movement.direction === 'in' ? 'success' : 'danger'" size="small">
											{{ movement.direction === 'in' ? 'In' : 'Uit' }}
										</vs-chip>
									</td>
									<td class="text-center p-4">{{ movement.quantity }}</td>
									<td class="p-4">{{ movement.reason || '-' }}</td>
								</tr>
								</tbody>
							</table>

							<div v-if="movementsTotalPages > 1" class="mt-5 flex justify-center">
								<vs-pagination
									:page.sync="movementsPage"
									:length="movementsTotalPages"
									@input="loadPoolMovements"
								></vs-pagination>
							</div>
						</div>
					</div>
				</vx-card>

				<vs-popup scrollable :title="`EAN aanpassen`" :active.sync="showEditEanDialog">
					<div class="vx-row">
						<div class="vx-col w-full mb-5" v-if="editingEan">
							<label class="label-text">Ean</label>
							<vs-input
								v-model="editingEan.ean"
								type="text"
								disabled=""
							></vs-input>
						</div>


						<div class="vx-col w-full mb-5">
							<label class="label-text">Gewicht in pool</label>
							<vs-input
								v-model.number="editingEanForm.weight"
								type="number"
								step="0.01"
							></vs-input>
						</div>

						<div class="vx-col w-full sm:w-1/2 mb-5">
							<label class="label-text">Max Voorraad</label>
							<vs-input
								v-model.number="editingEanForm.max_stock"
								type="number"
								step="1"
							></vs-input>
						</div>

						<div class="vx-col w-full sm:w-1/2 mb-5">
							<label class="label-text">Min Voorraad</label>
							<vs-input
								v-model.number="editingEanForm.min_stock"
								type="number"
								step="1"
							></vs-input>
						</div>

						<div class="vx-col w-full mb-5">
							<label class="label-text">Bundel aantal</label>
							<vs-input
								v-model.number="editingEanForm.bundle_multiplier"
								type="number"
								step="1"
							></vs-input>
						</div>

						<div class="vx-col w-full mb-5">
							<vs-checkbox v-model="editingEanForm.is_enabled">
								actief
							</vs-checkbox>
						</div>
					</div>

					<div class="flex justify-end gap-2 mt-6">
						<vs-button @click="closeEditEanDialog" color="danger" class="mr-2">Annuleren</vs-button>
						<vs-button @click="saveEditEan" color="primary" type="filled" :loading="isSavingEan">Opslaan</vs-button>
					</div>
				</vs-popup>

				<vs-popup scrollable title="Nieuwe EAN toevoegen" :active.sync="showCreateEanDialog">
					<div class="vx-row">
						<div class="vx-col w-full mb-5">
							<label class="label-text">EAN Code</label>
							<vs-input
								v-model="createEanForm.ean"
								type="text"
								placeholder="EAN code"
							></vs-input>
						</div>

						<div class="vx-col w-full mb-5">
							<label class="label-text">Gewicht in pool</label>
							<vs-input
								v-model.number="createEanForm.weight"
								type="number"
								step="1"
								min="1"
							></vs-input>
						</div>

						<div class="vx-col w-full sm:w-1/2 mb-5">
							<label class="label-text">Max Voorraad</label>
							<vs-input
								v-model.number="createEanForm.max_stock"
								type="number"
								step="1"
							></vs-input>
						</div>

						<div class="vx-col w-full sm:w-1/2 mb-5">
							<label class="label-text">Min Voorraad</label>
							<vs-input
								v-model.number="createEanForm.min_stock"
								type="number"
								step="1"
							></vs-input>
						</div>

						<div class="vx-col w-full mb-5">
							<label class="label-text">Bundel aantal</label>
							<vs-input
								v-model.number="createEanForm.bundle_multiplier"
								type="number"
								step="1"
							></vs-input>
						</div>

						<div class="vx-col w-full mb-5">
							<vs-checkbox v-model="createEanForm.is_enabled">
								actief
							</vs-checkbox>
						</div>
					</div>

					<div class="flex justify-end gap-2 mt-6">
						<vs-button @click="closeCreateEanDialog" color="danger" class="mr-2">Annuleren</vs-button>
						<vs-button @click="saveCreateEan" color="primary" type="filled" :loading="isSavingCreateEan">Aanmaken</vs-button>
					</div>
				</vs-popup>

				<vs-popup scrollable title="Pool aanpassen" :active.sync="showEditPoolDialog">
					<div class="vx-row">
						<div class="vx-col w-full mb-5">
							<label class="label-text">Naam</label>
							<vs-input
								v-model="editForm.name"
								type="text"
								placeholder="Pool naam"
							></vs-input>
						</div>

						<div class="vx-col w-full mb-5">
							<label class="label-text">Beschrijving</label>
							<vs-input
								v-model="editForm.description"
								type="textarea"
								placeholder="Beschrijving (optioneel)"
							></vs-input>
						</div>

						<div class="vx-col w-full mb-5">
							<label class="label-text">Veiligheidsvoorraad</label>
							<vs-input
								v-model.number="editForm.safety_stock"
								type="number"
							></vs-input>
						</div>
					</div>

					<div class="flex justify-end gap-2 mt-6">
						<vs-button @click="closeEditPoolDialog" color="danger" class="mr-2">Annuleren</vs-button>
						<vs-button @click="savePool" color="primary" type="filled" :loading="isSaving">Opslaan</vs-button>
					</div>
				</vs-popup>

				<vs-popup scrollable title="Nieuwe wijziging toevoegen" :active.sync="showCreateMovementDialog">
					<div class="vx-row">
						<div class="vx-col w-full mb-5">
							<label class="label-text">Richting</label>
							<vue-multiselect
								v-model="selectedDirection"
								placeholder="Selecteer richting"
								:options="directionOptions"
								:show-labels="false"
								:allow-empty="false"
								track-by="value"
								label="name"
								@input="createMovementForm.direction = selectedDirection.value"
							></vue-multiselect>
						</div>

						<div class="vx-col w-full mb-5">
							<label class="label-text">Hoeveelheid</label>
							<vs-input
								v-model.number="createMovementForm.quantity"
								type="number"
								step="1"
							></vs-input>
						</div>

						<div class="vx-col w-full mb-5">
							<label class="label-text">Reden</label>
							<vs-input
								v-model="createMovementForm.reason"
								type="textarea"
								placeholder="Reden van wijziging (optioneel)"
							></vs-input>
						</div>
					</div>

					<div class="flex justify-end gap-2 mt-6">
						<vs-button @click="closeCreateMovementDialog" color="danger" class="mr-2">Annuleren</vs-button>
						<vs-button @click="saveCreateMovement" color="primary" type="filled" :loading="isSavingMovement">Toevoegen</vs-button>
					</div>
				</vs-popup>
			</div>

			<div v-else class="text-center py-10">
				<p class="text-grey">Pool niet gevonden</p>
				<router-link to="/products/pools">
					<vs-button color="primary" type="border" class="mt-4">Terug naar pools</vs-button>
				</router-link>
			</div>
		</div>

		<vs-popup title="EAN verwijderen" :active.sync="showDeleteEanDialog">
			<div class="vx-row">
				<div class="vx-col w-full">
					<p v-if="eanToDelete">Weet je zeker dat je EAN <strong>{{ eanToDelete.ean }}</strong> wil verwijderen?</p>
				</div>
			</div>

			<div class="flex justify-end gap-2 mt-6">
				<vs-button @click="closeDeleteEanDialog" color="primary" class="mr-2">Annuleren</vs-button>
				<vs-button @click="confirmDeleteEan" color="danger" type="filled" :loading="isDeletingEan">Verwijderen</vs-button>
			</div>
		</vs-popup>
	</div>
</template>

<script>
import {mapGetters} from 'vuex';
import apiClient from '@/services/apiClient';

export default {
	name: 'poolEdit',
	data() {
		return {
			loading: true,
			isSaving: false,
			pool: null,
			showEditPoolDialog: false,
			editForm: {
				name: '',
				description: '',
				safety_stock: 0
			},
			showEditEanDialog: false,
			isSavingEan: false,
			editingEan: null,
			editingEanForm: {
				weight: 0,
				max_stock: 0,
				min_stock: 0,
				is_enabled: true,
				bundle_multiplier: 1
			},
			showDeleteEanDialog: false,
			isDeletingEan: false,
			eanToDelete: null,
			showCreateEanDialog: false,
			isSavingCreateEan: false,
			createEanForm: {
				ean: '',
				weight: 1,
				max_stock: 999,
				min_stock: 0,
				is_enabled: true,
				bundle_multiplier: 1
			},

			poolMovements: [],
			loadingMovements: false,
			movementsPage: 1,
			movementsTotalPages: 1,
			newMovementIds: [],
			movementsRefreshInterval: null,
			showCreateMovementDialog: false,
			isSavingMovement: false,
			createMovementForm: {
				direction: 'in',
				quantity: 0,
				reason: ''
			},
			directionOptions: [
				{name: 'Inkomend', value: 'in'},
				{name: 'Uitgaand', value: 'out'}
			],
			selectedDirection: {name: 'Inkomend', value: 'in'}
		}
	},
	computed: {
		...mapGetters({
			moduleEnabled: 'dashboard/moduleStockPoolsEnabled',
			dashboardInitialized: 'dashboard/initialized'
		})
	},
	watch: {
		dashboardInitialized(newVal) {
			if (newVal) {
				this.initPool();
				this.startMovementsRefresh();
			}
		}
	},
	mounted() {
		if (this.dashboardInitialized) {
			this.initPool();
			this.startMovementsRefresh();
		}
	},
	beforeDestroy() {
		this.stopMovementsRefresh();
	},
	methods: {
		async initPool() {
			this.$vs.loading();
			const poolId = this.$route.params.id;
			try {
				const response = await apiClient.get(`stockpools/get/${poolId}`);
				const poolData = response.data;

				this.pool = poolData;
				this.editForm = {
					name: this.pool.name,
					description: this.pool.description || '',
					safety_stock: this.pool.safety_stock || 0
				};

				this.loadPoolMovements(true);
			} catch (error) {
				this.pool = null;
			} finally {
				this.loading = false;
				this.$vs.loading.close();
			}
		},

		async refreshPool() {
			const poolId = this.$route.params.id;
			try {
				const response = await apiClient.get(`stockpools/get/${poolId}`);
				this.pool = response.data;
				this.editForm = {
					name: this.pool.name,
					description: this.pool.description || '',
					safety_stock: this.pool.safety_stock || 0
				};

			} catch (error) {

			}
		},

		async savePool() {
			if (!this.editForm.name || this.editForm.name.trim() === '') {
				this.$vs.notify({
					title: 'Validatie',
					text: 'Pool naam is verplicht',
					color: 'warning'
				});
				return;
			}

			this.isSaving = true;
			try {
				await this.$store.dispatch('poolsData/updatePool', {
					poolId: this.pool.id,
					poolData: {
						name: this.editForm.name,
						description: this.editForm.description,
						safety_stock: this.editForm.safety_stock
					}
				});

				// Update pool object directly
				this.pool.name = this.editForm.name;
				this.pool.description = this.editForm.description;
				this.pool.safety_stock = this.editForm.safety_stock;

				this.$vs.notify({
					title: 'Gelukt',
					text: 'Pool is aangepast',
					color: 'success'
				});

				this.closeEditPoolDialog();
			} catch (error) {
				this.$vs.notify({
					title: 'Oops',
					text: 'Pool kon niet worden aangepast',
					color: 'danger'
				});
			} finally {
				this.isSaving = false;
			}
		},

		openEditPoolDialog() {
			this.editForm = {
				name: this.pool.name,
				description: this.pool.description || '',
				safety_stock: this.pool.safety_stock || 0
			};
			this.showEditPoolDialog = true;
		},

		closeEditPoolDialog() {
			this.showEditPoolDialog = false;
			this.editForm = {
				name: '',
				description: '',
				central_stock: 0,
				safety_stock: 0
			};
		},

		openEditEanDialog(ean) {
			this.editingEan = ean;
			this.editingEanForm = {
				weight: ean.weight,
				max_stock: ean.max_stock,
				min_stock: ean.min_stock,
				is_enabled: ean.is_enabled,
				bundle_multiplier: ean.bundle_multiplier
			};
			this.showEditEanDialog = true;
		},

		closeEditEanDialog() {
			this.showEditEanDialog = false;
			this.editingEan = null;
			this.editingEanForm = {
				weight: 0,
				max_stock: 0,
				min_stock: 0,
				is_enabled: true,
				bundle_multiplier: 1
			};
		},

		openDeleteEanDialog(ean) {
			this.eanToDelete = ean;
			this.showDeleteEanDialog = true;
		},

		closeDeleteEanDialog() {
			this.showDeleteEanDialog = false;
			this.eanToDelete = null;
		},

		async confirmDeleteEan() {
			this.isDeletingEan = true;
			try {
				await apiClient.delete(`stockpools/${this.pool.id}/eans/delete/${this.eanToDelete.id}`);

				// Remove the EAN from the pool.pool_eans array
				const eanIndex = this.pool.pool_eans.findIndex(e => e.id === this.eanToDelete.id);
				if (eanIndex > -1) {
					this.pool.pool_eans.splice(eanIndex, 1);
				}

				this.$vs.notify({
					title: 'Gelukt',
					text: 'EAN is verwijderd',
					color: 'success'
				});

				this.closeDeleteEanDialog();
				this.refreshPool();
			} catch (error) {
				this.$vs.notify({
					title: 'Oops',
					text: 'EAN kon niet worden verwijderd',
					color: 'danger'
				});
			} finally {
				this.isDeletingEan = false;
			}
		},

		async saveEditEan() {
			this.isSavingEan = true;
			try {
				await apiClient.put(`stockpools/${this.pool.id}/eans/update/${this.editingEan.id}`, {
					weight: this.editingEanForm.weight,
					max_stock: this.editingEanForm.max_stock,
					min_stock: this.editingEanForm.min_stock,
					is_enabled: this.editingEanForm.is_enabled,
					bundle_multiplier: this.editingEanForm.bundle_multiplier
				});

				// Update the EAN in the pool.pool_eans array
				const eanIndex = this.pool.pool_eans.findIndex(e => e.id === this.editingEan.id);
				if (eanIndex > -1) {
					this.pool.pool_eans[eanIndex] = {
						...this.pool.pool_eans[eanIndex],
						...this.editingEanForm
					};
				}

				this.$vs.notify({
					title: 'Gelukt',
					text: 'EAN is aangepast',
					color: 'success'
				});

				this.closeEditEanDialog();
			} catch (error) {
				this.$vs.notify({
					title: 'Oops',
					text: 'EAN kon niet worden aangepast',
					color: 'danger'
				});
			} finally {
				this.isSavingEan = false;
			}
		},

		formatDate(dateString) {
			if (!dateString) return '-';
			const date = new Date(dateString);
			return date.toLocaleDateString('nl-NL', {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit'
			});
		},

		openCreateEanDialog() {
			this.createEanForm = {
				ean: '',
				weight: 1,
				max_stock: 999,
				min_stock: 0,
				is_enabled: true,
				bundle_multiplier: 1
			};
			this.showCreateEanDialog = true;
		},

		closeCreateEanDialog() {
			this.showCreateEanDialog = false;
			this.createEanForm = {
				ean: '',
				weight: 1,
				max_stock: 999,
				min_stock: 0,
				is_enabled: true,
				bundle_multiplier: 1
			};
		},

		async saveCreateEan() {
			if (!this.createEanForm.ean || this.createEanForm.ean.trim() === '') {
				this.$vs.notify({
					title: 'Validatie',
					text: 'EAN code is verplicht',
					color: 'warning'
				});
				return;
			}

			this.isSavingCreateEan = true;
			try {
				const response = await apiClient.post(`stockpools/${this.pool.id}/eans/create`, {
					ean: this.createEanForm.ean,
					weight: this.createEanForm.weight,
					max_stock: this.createEanForm.max_stock,
					min_stock: this.createEanForm.min_stock,
					is_enabled: this.createEanForm.is_enabled,
					bundle_multiplier: this.createEanForm.bundle_multiplier
				});

				//refresh list
				this.refreshPool();

				this.$vs.notify({
					title: 'Gelukt',
					text: 'EAN is toegevoegd',
					color: 'success'
				});

				this.closeCreateEanDialog();
			} catch (error) {
				this.$vs.notify({
					title: 'Oops',
					text: 'EAN kon niet worden toegevoegd, mogelijk bestaat deze EAN al in een van je pools',
					color: 'danger'
				});
			} finally {
				this.isSavingCreateEan = false;
			}
		},

		async loadPoolMovements(initial = false) {
			this.loadingMovements = true;
			try {
				const response = await apiClient.get(`stockpools/${this.pool.id}/movements/list?page=${this.movementsPage}&limit=10`);
				const oldIds = this.poolMovements.map(m => m.id);
				this.poolMovements = response.data.movements || [];
				this.movementsTotalPages = Math.ceil((response.data.total || 0) / 10);

				// Detect new movements
				const newIds = this.poolMovements
					.filter(m => !oldIds.includes(m.id))
					.map(m => m.id);

				if (newIds.length > 0) {
					if (!initial) {
						this.newMovementIds = newIds;
						// Animate for 3 seconds
						setTimeout(() => {
							this.newMovementIds = [];
						}, 3000);
					}

					// Refresh pool data since stock levels may have changed
					this.refreshPool();
				}
			} catch (error) {
				console.error('Error loading movements:', error);
			} finally {
				this.loadingMovements = false;
			}
		},

		startMovementsRefresh() {
			// Initial load
			this.loadPoolMovements();

			// Refresh every 10 seconds
			this.movementsRefreshInterval = setInterval(() => {
				this.loadPoolMovements();
			}, 10000);
		},

		stopMovementsRefresh() {
			if (this.movementsRefreshInterval) {
				clearInterval(this.movementsRefreshInterval);
				this.movementsRefreshInterval = null;
			}
		},

		calculateEanAllocations() {
			if (!this.pool || !this.pool.pool_eans) {
				return {};
			}

			// Step 1: Filter enabled EANs and sort by id
			const enabledEans = this.pool.pool_eans
				.filter(ean => ean.is_enabled)
				.sort((a, b) => a.id - b.id);

			if (enabledEans.length === 0) {
				return {};
			}

			// Step 2: Calculate available pool stock
			const poolAvailable = Math.max(
				0,
				(this.pool.central_stock || 0) - (this.pool.safety_stock || 0)
			);

			if (poolAvailable <= 0) {
				const result = {};
				enabledEans.forEach(ean => {
					result[ean.id] = 0;
				});
				return result;
			}

			const allocations = {};
			let remaining = poolAvailable;

			// Iteratively allocate, respecting max_stock constraints
			while (remaining > 0) {
				let sumWeights = 0;
				const canAllocateEans = [];

				// Find EANs that can still receive stock (haven't hit max_stock)
				for (const ean of enabledEans) {
					const currentAlloc = allocations[ean.id] || 0;
					const canAllocate = ean.max_stock === null || ean.max_stock === undefined || currentAlloc < ean.max_stock;

					if (canAllocate) {
						sumWeights += (ean.weight || 0);
						canAllocateEans.push(ean);
					}
				}

				// If no EANs can allocate more, we're done
				if (canAllocateEans.length === 0 || sumWeights <= 0) {
					break;
				}

				const oldRemaining = remaining;

				// Allocate proportionally among EANs that can still receive
				for (const ean of canAllocateEans) {
					const currentAlloc = allocations[ean.id] || 0;

					// Calculate ideal share of remaining stock
					const idealShare = Math.floor((remaining * (ean.weight || 0)) / sumWeights);

					// Cap to max_stock if set
					const canReceive = ean.max_stock !== null && ean.max_stock !== undefined
						? Math.min(idealShare, ean.max_stock - currentAlloc)
						: idealShare;

					const finalCanReceive = Math.max(0, canReceive);

					allocations[ean.id] = (allocations[ean.id] || 0) + finalCanReceive;
					remaining -= finalCanReceive;
				}

				// Prevent infinite loops if no progress was made
				if (remaining === oldRemaining) {
					break;
				}
			}

			// Ensure all EANs have an allocation (even if 0)
			for (const ean of enabledEans) {
				if (!(ean.id in allocations)) {
					allocations[ean.id] = 0;
				}
			}

			return allocations;
		},

		calculateEanStock(ean) {
			const allocations = this.calculateEanAllocations();
			return allocations[ean.id] || 0;
		},

		calculateTotalEanStock() {
			const allocations = this.calculateEanAllocations();
			return Object.values(allocations).reduce((sum, val) => sum + val, 0);
		},

		calculateTotalAanbodStock() {
			if (!this.pool || !this.pool.pool_eans) return 0;

			return this.pool.pool_eans.reduce((total, ean) => {
				const stock = this.calculateEanStock(ean);
				const bundleMultiplier = ean.bundle_multiplier || 1;
				const usedStock = Math.floor(stock / bundleMultiplier);
				return total + (usedStock * bundleMultiplier);
			}, 0);
		},

		openCreateMovementDialog() {
			this.createMovementForm = {
				direction: 'in',
				quantity: 0,
				reason: ''
			};
			this.selectedDirection = {name: 'Inkomend', value: 'in'};
			this.showCreateMovementDialog = true;
		},

		closeCreateMovementDialog() {
			this.showCreateMovementDialog = false;
			this.createMovementForm = {
				direction: 'in',
				quantity: 0,
				reason: ''
			};
			this.selectedDirection = {name: 'Inkomend', value: 'in'};
		},

		async saveCreateMovement() {
			if (this.createMovementForm.quantity <= 0) {
				this.$vs.notify({
					title: 'Validatie',
					text: 'Hoeveelheid moet groter zijn dan 0',
					color: 'warning'
				});
				return;
			}

			this.isSavingMovement = true;
			try {
				await apiClient.post(`stockpools/${this.pool.id}/movements/create`, {
					direction: this.createMovementForm.direction,
					quantity: this.createMovementForm.quantity,
					reason: this.createMovementForm.reason
				});

				this.$vs.notify({
					title: 'Gelukt',
					text: 'Wijziging is toegevoegd',
					color: 'success'
				});

				this.closeCreateMovementDialog();
				this.loadPoolMovements();
			} catch (error) {
				this.$vs.notify({
					title: 'Oops',
					text: 'Wijziging kon niet worden toegevoegd',
					color: 'danger'
				});
			} finally {
				this.isSavingMovement = false;
			}
		}
	},

}
</script>

<style scoped>
.flex {
	display: flex;
}

.justify-end {
	justify-content: flex-end;
}

.gap-2 {
	gap: 0.5rem;
}

.mt-4 {
	margin-top: 1rem;
}

.movements-table {
	border-collapse: collapse;
	background: white;
}

.movements-table thead {
	background-color: #fafafa;
	font-weight: 600;
	font-size: 0.875rem;
}

.movements-table th {
	text-align: left;
	padding: 1rem;
	color: #6c757d;
}

.movement-row:hover {
	background-color: #f9f9f9;
}

.eans-table {
	border-collapse: collapse;
	background: white;
}

.eans-table thead {
	background-color: #fafafa;
	font-weight: 600;
	font-size: 0.875rem;
}

.eans-table th {
	text-align: left;
	padding: 1rem;
	color: #6c757d;
}

.border-b {
	border-bottom: 1px solid #e5e7eb;
}

.border-b-2 {
	border-bottom: 2px solid #e5e7eb;
}

.text-grey {
	color: #9ca3af;
}

.text-center {
	text-align: center;
}

.text-left {
	text-align: left;
}

.pl-2 {
	padding-left: 0.5rem;
}

.mr-2 {
	margin-right: 0.5rem;
}

.mb-5 {
	margin-bottom: 1.25rem;
}

.mt-6 {
	margin-top: 1.5rem;
}

.py-10 {
	padding-top: 2.5rem;
	padding-bottom: 2.5rem;
}

.w-full {
	width: 100%;
}

.new-movement {
	animation: highlight 3s ease-in-out;
}

@keyframes highlight {
	0% {
		background-color: #fffbeb;
	}
	50% {
		background-color: #fef3c7;
	}
	100% {
		background-color: transparent;
	}
}

.stock-summary {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
	gap: 1.5rem;
}

.stock-item {
	padding: 1rem;
	border-radius: 0.5rem;
	background-color: #f9fafb;
	border: 1px solid #e5e7eb;
	text-align: center;
	transition: all 0.3s ease;
}

.stock-item:hover {
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

.stock-item.highlight {
	background-color: #ecfdf5;
	border: 2px solid #10b981;
}

.stock-item.info {
	background-color: rgba(195, 210, 237, 0.55);
	border: 2px solid rgba(59, 113, 202, 0.55);
}

.stock-label {
	font-size: 0.875rem;
	color: #6b7280;
	font-weight: 500;
	margin-bottom: 0.5rem;
}

.stock-value {
	font-size: 1.875rem;
	font-weight: 700;
}

.stock-value.primary {
	color: #3b82f6;
}

.stock-value.warning {
	color: #f59e0b;
}

.stock-value.info {
	color: #8b5cf6;
}

.stock-value.success {
	color: #10b981;
}

.label-text {
	display: block;
	font-size: 0.875rem;
	font-weight: 500;
	color: #6b7280;
	margin-bottom: 0.5rem;
}

.pool-info-text {
	font-size: 1rem;
	color: #1f2937;
	margin: 0;
}

.font-bold {
	font-weight: 700;
}

.border-t-2 {
	border-top: 2px solid #e5e7eb;
}

.totals-row {
	background-color: #f3f4f6;
}

.text-right {
	text-align: right;
}
</style>
